import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import CONSTANTS from "../utils/constants";

export default function AdminBannerEdit() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    order_number: 0,
    image: null,
    category: "home",
    isActive: true,
    link_type: "none",
    link_id: "",
    link_subcategory_id: "",
  });

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [loading, setLoading] = useState({
    banner: true,
    categories: false,
    products: false,
  });

  const mainCategories = categories.filter((cat) => cat.parent_id === null);
  const allSubcategories = categories.filter((cat) => cat.parent_id !== null);

  const mainCategoryOptions = mainCategories.map((cat) => ({
    value: cat.id,
    label: cat.name_tm || cat.name_ru || cat.name_en || `Kategori ${cat.id}`,
  }));

  const subcategoryOptions = allSubcategories
    .filter((subcat) =>
      formData.link_id
        ? subcat.parent_id === parseInt(formData.link_id)
        : true
    )
    .map((subcat) => ({
      value: subcat.id,
      label:
        subcat.name_tm ||
        subcat.name_ru ||
        subcat.name_en ||
        `SubCategory ${subcat.id}`,
    }));

  const productOptions = products.map((product) => ({
    value: product.id,
    label: `${product.name_tm || product.name_ru || product.name_en || "Ürün"} | (${product.barcode || "Barkod Yok"})`,
    category: product.category_id,
    subcategory: product.subcategory_id,
  }));

  const selectedMainCategory =
    mainCategoryOptions.find(
      (opt) => opt.value === parseInt(formData.link_id)
    ) || null;

  // ✅ Düzeltme: categories array'inden direkt hesapla, subcategories state'ine bağımlı değil
  const selectedSubcategory = (() => {
    if (!formData.link_subcategory_id) return null;
    const subcat = categories.find(
      (cat) => cat.id === parseInt(formData.link_subcategory_id)
    );
    if (!subcat) return null;
    return {
      value: subcat.id,
      label:
        subcat.name_tm ||
        subcat.name_ru ||
        subcat.name_en ||
        `SubCategory ${subcat.id}`,
    };
  })();

  const selectedProduct = (() => {
    if (!formData.link_id) return null;
    const product = products.find((p) => p.id === parseInt(formData.link_id));
    if (!product) return null;
    return {
      value: product.id,
      label: `${product.name_tm || product.name_ru || product.name_en || "Ürün"} | (${product.barcode || "Barkod Yok"})`,
      category: product.category_id,
      subcategory: product.subcategory_id,
    };
  })();

  // Kategorileri getir
  const fetchCategories = async () => {
    if (categories.length > 0) return categories;

    setLoading((prev) => ({ ...prev, categories: true }));
    try {
      const res = await axios.get(`${CONSTANTS.baseRoute}/categories`);
      setCategories(res.data);
      return res.data;
    } catch (error) {
      console.error("Error fetching categories:", error);
      return [];
    } finally {
      setLoading((prev) => ({ ...prev, categories: false }));
    }
  };

  useEffect(() => {
    const fetchBanner = async () => {
      try {
        const res = await axios.get(`${CONSTANTS.baseRoute}/banners/${id}`);
        const banner = res.data;

        setFormData({
          name: banner.name || "",
          order_number: banner.order_number || 0,
          image: null,
          category: banner.category || "home",
          isActive: banner.is_active ?? true,
          link_type: banner.link_type || "none",
          link_id: banner.link_id || "",
          link_subcategory_id: banner.link_subcategory_id || "",
        });

        if (banner.image) {
          setImagePreview(`${CONSTANTS.baseRoute}${banner.image}`);
        }

        await fetchCategories();
      } catch (error) {
        console.error("Error fetching banner:", error);
        setMessage({
          type: "danger",
          text:
            t("adminBannerEdit.fetchError") ||
            "Banner maglumatlaryny alyp bolmady!",
        });
        setTimeout(() => navigate("/admin/home/banner"), 2000);
      } finally {
        setLoading((prev) => ({ ...prev, banner: false }));
      }
    };

    fetchBanner();
  }, [id, navigate, t]);

  // Ana kategori değişince alt kategorileri güncelle — ama link_subcategory_id'yi sıfırlama (ilk yüklemede)
  useEffect(() => {
    if (formData.link_type === "subcategory" && formData.link_id) {
      const subs = categories.filter(
        (cat) => cat.parent_id === parseInt(formData.link_id)
      );
      setSubcategories(subs);
    } else if (formData.link_type !== "subcategory") {
      setSubcategories([]);
    }
  }, [formData.link_id, formData.link_type, categories]);

  // Ürünleri getir
  const fetchProducts = async () => {
    if (products.length > 0) return;

    setLoading((prev) => ({ ...prev, products: true }));
    try {
      const res = await axios.get(`${CONSTANTS.baseRoute}/products/admin`);
      setProducts(res.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading((prev) => ({ ...prev, products: false }));
    }
  };

  // Link tipi değiştiğinde
  useEffect(() => {
    if (
      formData.link_type === "category" ||
      formData.link_type === "subcategory"
    ) {
      fetchCategories();
    } else if (formData.link_type === "product") {
      fetchProducts();
    } else {
      setSubcategories([]);
    }
  }, [formData.link_type]);

  useEffect(() => {
    if (formData.link_type === "product" && products.length > 0) {
      if (formData.link_subcategory_id) {
        setFilteredProducts(
          products.filter(
            (p) => p.subcategory_id === parseInt(formData.link_subcategory_id)
          )
        );
      } else if (formData.link_id) {
        setFilteredProducts(
          products.filter(
            (p) => p.category_id === parseInt(formData.link_id)
          )
        );
      } else {
        setFilteredProducts(products);
      }
    }
  }, [formData.link_id, formData.link_subcategory_id, products, formData.link_type]);

  const handleInputChange = (e) => {
    const { id, value, type, checked } = e.target;

    if (id === "link_type") {
      setFormData((prev) => ({
        ...prev,
        [id]: value,
        link_id: "",
        link_subcategory_id: "",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [id]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleMainCategoryChange = (selected) => {
    setFormData((prev) => ({
      ...prev,
      link_id: selected ? selected.value.toString() : "",
      link_subcategory_id: "", // Ana kategori değişince alt kategoriyi sıfırla
    }));
  };

  const handleSubcategoryChange = (selected) => {
    setFormData((prev) => ({
      ...prev,
      link_subcategory_id: selected ? selected.value.toString() : "",
    }));
  };

  const handleProductChange = (selected) => {
    setFormData((prev) => ({
      ...prev,
      link_id: selected ? selected.value.toString() : "",
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setMessage({
          type: "danger",
          text: t("adminBannerAdd.onlyImagesAllowed"),
        });
        return;
      }
      setFormData((prev) => ({ ...prev, image: file }));
      setImagePreview(URL.createObjectURL(file));
      setMessage({ type: "", text: "" });
    }
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setMessage({ type: "danger", text: t("adminBannerAdd.nameRequired") });
      return false;
    }

    if (!imagePreview && !formData.image) {
      setMessage({ type: "danger", text: t("adminBannerAdd.imageRequired") });
      return false;
    }

    if (formData.link_type !== "none") {
      if (formData.link_type === "subcategory") {
        if (!formData.link_id || !formData.link_subcategory_id) {
          setMessage({
            type: "danger",
            text:
              t("adminBannerEdit.linkRequired") ||
              "Lütfen bir ana kategori ve alt kategori seçin",
          });
          return false;
        }
      } else if (!formData.link_id) {
        setMessage({
          type: "danger",
          text:
            t("adminBannerEdit.linkRequired") ||
            "Lütfen bir kategori veya ürün seçin",
        });
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setMessage({ type: "", text: "" });
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name.trim());
      formDataToSend.append("order_number", formData.order_number);
      formDataToSend.append("category", formData.category);
      formDataToSend.append("is_active", formData.isActive);
      formDataToSend.append("link_type", formData.link_type);

      if (formData.link_id) {
        formDataToSend.append("link_id", formData.link_id);
      }
      if (formData.link_subcategory_id) {
        formDataToSend.append("link_subcategory_id", formData.link_subcategory_id);
      }
      if (formData.image) {
        formDataToSend.append("image", formData.image);
      }

      await axios.put(`${CONSTANTS.baseRoute}/banners/${id}`, formDataToSend, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setMessage({
        type: "success",
        text:
          t("adminBannerEdit.successMessage") ||
          "Banner üstünlikli üýtgedildi!",
      });

      setTimeout(() => navigate("/admin/home/banner"), 1500);
    } catch (error) {
      console.error("Error updating banner:", error);
      setMessage({
        type: "danger",
        text:
          error.response?.data?.message ||
          t("adminBannerEdit.submitError") ||
          "Banner üýtgetmekde näsazlyk ýüze çykdy.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading.banner) {
    return (
      <div className="admin-main p-2 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "58px",
      borderRadius: "0.375rem",
      borderColor: state.isFocused ? "#F47D39" : "#cccc",
      boxShadow: state.isFocused
        ? "0 0 0 0.25rem rgba(244, 125, 57, 0.25)"
        : null,
      "&:hover": { borderColor: "#F47D39" },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#F47D39"
        : state.isFocused
          ? "#e9ecef"
          : null,
      color: state.isSelected ? "white" : "black",
    }),
  };

  return (
    <div className="admin-main p-2">
      <div className="d-flex gap-2 align-items-center mb-3">
        <button className="btn btn-light" onClick={() => navigate(-1)}>
          <i className="bi bi-chevron-left"></i>
        </button>
        <button className="btn btn-light" onClick={() => navigate(+1)}>
          <i className="bi bi-chevron-right"></i>
        </button>
        <h5 className="fw-bold mb-0">
          {t("adminBannerEdit.title") || "Banner Üýtgetmek"}
        </h5>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type} shadow-sm mb-4`} role="alert">
          {message.text}
        </div>
      )}

      <div className="card shadow-sm border-0 rounded">
        <div className="card-body p-4">
          <form onSubmit={handleSubmit}>
            <div className="row g-4">
              <div className="col-md-6">
                <div className="form-floating">
                  <input
                    type="text"
                    className="form-control rounded-3"
                    id="name"
                    placeholder={t("adminBannerAdd.namePlaceholder")}
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                  <label htmlFor="name">
                    {t("adminBannerAdd.name")}{" "}
                    <span className="text-danger">*</span>
                  </label>
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-floating">
                  <input
                    type="number"
                    className="form-control rounded-3"
                    id="order_number"
                    placeholder={t("adminBannerAdd.orderPlaceholder")}
                    value={formData.order_number}
                    onChange={handleInputChange}
                    min="0"
                  />
                  <label htmlFor="order_number">
                    {t("adminBannerAdd.order")}
                  </label>
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-floating">
                  <select
                    className="form-select rounded-3"
                    id="link_type"
                    value={formData.link_type}
                    onChange={handleInputChange}
                  >
                    <option value="none">
                      {t("adminBannerEdit.noLink") || "Bağlantı Yok"}
                    </option>
                    <option value="category">
                      {t("adminBannerEdit.linkToCategory") || "Kategoriye Bağla"}
                    </option>
                    <option value="subcategory">
                      {t("adminBannerEdit.linkToSubcategory") || "Alt Kategoriye Bağla"}
                    </option>
                    <option value="product">
                      {t("adminBannerEdit.linkToProduct") || "Ürüne Bağla"}
                    </option>
                  </select>
                  <label htmlFor="link_type">
                    {t("adminBannerEdit.linkType") || "Bağlantı Tipi"}
                  </label>
                </div>
              </div>

              {(formData.link_type === "category" ||
                formData.link_type === "subcategory") && (
                <div className="col-6">
                  <Select
                    options={mainCategoryOptions}
                    value={selectedMainCategory}
                    onChange={handleMainCategoryChange}
                    placeholder={
                      loading.categories
                        ? "Yükleniyor..."
                        : t("adminBannerEdit.selectCategory") || "Kategori Seçin"
                    }
                    isClearable
                    isSearchable
                    styles={selectStyles}
                    isLoading={loading.categories}
                    noOptionsMessage={() => "Kategori bulunamadı"}
                  />
                </div>
              )}

              {formData.link_type === "subcategory" && formData.link_id && (
                <div className="col-12">
                  <Select
                    options={subcategoryOptions}
                    value={selectedSubcategory}  // ✅ artık categories'den direkt hesaplanıyor
                    onChange={handleSubcategoryChange}
                    placeholder={
                      subcategoryOptions.length === 0
                        ? "Bu kategoride alt kategori yok"
                        : t("adminBannerEdit.selectSubcategory") || "Alt Kategori Seçin"
                    }
                    isClearable
                    isSearchable
                    styles={selectStyles}
                    noOptionsMessage={() => "Alt kategori bulunamadı"}
                    isDisabled={subcategoryOptions.length === 0}
                  />
                  {subcategoryOptions.length === 0 && (
                    <small className="text-muted">
                      Bu ana kategoride alt kategori bulunmamaktadır.
                    </small>
                  )}
                </div>
              )}

              {formData.link_type === "product" && (
                <div className="col-6">
                  <Select
                    options={productOptions}
                    value={selectedProduct}
                    onChange={handleProductChange}
                    placeholder={
                      loading.products
                        ? "Yükleniyor..."
                        : t("adminBannerEdit.selectProduct") || "Ürün Seçin"
                    }
                    isClearable
                    isSearchable
                    styles={selectStyles}
                    isLoading={loading.products}
                    noOptionsMessage={() => "Ürün bulunamadı"}
                  />
                </div>
              )}

              <div className="col-12">
                <label htmlFor="image" className="form-label fw-semibold small">
                  {t("adminBannerAdd.image")}{" "}
                  <span className="text-danger">*</span>
                  <small className="text-muted ms-2">
                    ({t("adminBannerAdd.recommendedSize") || "847×423"})
                  </small>
                </label>

                <input
                  type="file"
                  className="form-control rounded-3"
                  id="image"
                  accept="image/*"
                  onChange={handleImageChange}
                />

                {imagePreview && (
                  <div className="mt-3 overflow-hidden">
                    <img
                      src={imagePreview}
                      alt={t("adminBannerAdd.preview")}
                      className="img-fluid w-100"
                      style={{ maxHeight: "280px", objectFit: "contain" }}
                    />
                  </div>
                )}
              </div>

              <div className="col-md-6 d-flex align-items-center">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                  <label className="form-check-label fw-semibold" htmlFor="isActive">
                    {t("adminBannerAdd.active")}
                  </label>
                </div>
              </div>

              <div className="col-12 d-flex justify-content-end mt-4">
                <button
                  type="submit"
                  className="btn btn-primary px-5 py-2 fw-bold rounded-3"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      {t("adminBannerEdit.submitting") || "Täzelenýär..."}
                    </>
                  ) : (
                    <>
                      <i className="bi bi-save me-2"></i>
                      {t("adminBannerEdit.submit") || "Ýatda sakla"}
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
